import { useParams, useSearch, useNavigate } from '@tanstack/react-router';
import { PageContainer } from '../../components/layout/PageContainer.js';
import { Button } from '../../components/ui/Button.js';
import { useAuth } from '../auth/useAuth.js';
import { Breadcrumb } from './components/Breadcrumb.js';
import { MachineHeader } from './components/MachineHeader.js';
import { TabBar, type MachineTab } from './components/TabBar.js';
import { OverviewTab } from './tabs/OverviewTab.js';
import { TelemetryTab } from './tabs/TelemetryTab.js';
import { AlarmsTab } from './tabs/AlarmsTab.js';
import { ToolsTab } from './tabs/ToolsTab.js';
import { useMachineDetail } from './hooks/useMachineDetail.js';

export function MachineDetailPage(): JSX.Element {
  const { machineId } = useParams({ from: '/protected/machines/$machineId' });
  const { tab } = useSearch({ from: '/protected/machines/$machineId' });
  const navigate = useNavigate({ from: '/machines/$machineId' });
  const { shopId } = useAuth();
  const { machine, isLoading, isError, refetch } = useMachineDetail(shopId, machineId);

  const setTab = (next: MachineTab): void => {
    void navigate({ search: { tab: next }, replace: true });
  };

  return (
    <PageContainer>
      <Breadcrumb machineId={machineId} />

      {isLoading && !machine && <SkeletonHeader />}
      {isError && !machine && <ErrorState onRetry={refetch} />}

      {machine && (
        <>
          <MachineHeader machine={machine} />
          <TabBar active={tab} onChange={setTab} />
          {tab === 'overview' && <OverviewTab shopId={shopId} machine={machine} />}
          {tab === 'telemetry' && <TelemetryTab shopId={shopId} machineId={machineId} />}
          {tab === 'alarms' && <AlarmsTab shopId={shopId} machineId={machineId} />}
          {tab === 'tools' && <ToolsTab shopId={shopId} machineId={machineId} />}
        </>
      )}
    </PageContainer>
  );
}

function SkeletonHeader(): JSX.Element {
  return (
    <div className="space-y-3">
      <div className="h-7 w-48 rounded bg-card-2 animate-shimmer" />
      <div className="h-10 rounded bg-card-2 animate-shimmer" />
      <div className="h-32 rounded bg-card-2 animate-shimmer" />
    </div>
  );
}

function ErrorState({ onRetry }: { readonly onRetry: () => void }): JSX.Element {
  return (
    <div className="rounded-md border border-border bg-card p-6 text-center">
      <p className="text-sm text-red mb-2">Couldn&apos;t load this machine.</p>
      <Button variant="secondary" onClick={onRetry}>
        Retry
      </Button>
    </div>
  );
}
